import { STANDARD_PALLET_TYPE_VALUES } from '../../constants/pallets';
import { DIMENSION_UNITS } from '../../constants';
import { convertUnitToMetres } from '../client/AddProductForm/utils/format';
import type { UnitOfMeasure } from '../client/AddProductForm/utils/format';

// The pallet types the BACKEND resolves to a canonical footprint (must mirror the backend
// `StandardPalletDimensions` map). Two uses, both keyed off this single predicate:
//   - HIDE the manual length/width inputs (showing them would mislead — the controller
//     overrides with the standard footprint).
//   - keep length/width OPTIONAL in the Yup schema (the backend fills them).
// Everything else — Custom Pallet / Other / Unpalletised / Mixed GB/EU — is NOT resolved, so
// the inputs are shown and the user must provide the dimensions (Mixed behaves like Custom).
const BACKEND_RESOLVED_PALLET_TYPES = new Set<string>(
  STANDARD_PALLET_TYPE_VALUES
);

export function isBackendResolvedPalletType(
  palletType: string | undefined | null
): boolean {
  return !!palletType && BACKEND_RESOLVED_PALLET_TYPES.has(palletType);
}

const KILOGRAMS_PER_POUND = 0.453592;
const KILOGRAMS_PER_OUNCE = 0.0283495;
const KILOGRAMS_PER_GRAM = 0.001;

export function toLoadHeightCm(
  height: number | string | null | undefined,
  heightUnit: string | undefined | null
): number | null {
  const measured = Number(height);
  if (!Number.isFinite(measured) || measured <= 0) return null;
  if (!DIMENSION_UNITS.some((unit) => unit.value === heightUnit)) return null;
  return Math.round(
    convertUnitToMetres(measured, heightUnit as UnitOfMeasure) * 100
  );
}

function toKilograms(weight: number, weightUnit: string | undefined | null) {
  switch (weightUnit) {
    case 'lb':
      return weight * KILOGRAMS_PER_POUND;
    case 'oz':
      return weight * KILOGRAMS_PER_OUNCE;
    case 'g':
      return weight * KILOGRAMS_PER_GRAM;
    default:
      return weight;
  }
}

export function toTotalWeightKg(
  totalWeight: number | string | null | undefined,
  weightUnit: string | undefined | null
): number | null {
  const consignmentWeight = Number(totalWeight);
  if (!Number.isFinite(consignmentWeight) || consignmentWeight <= 0)
    return null;
  return toKilograms(consignmentWeight, weightUnit);
}

export function toPalletCount(
  totalPallets: number | string | null | undefined
): number | null {
  const pallets = Number(totalPallets);
  if (!Number.isFinite(pallets) || pallets <= 0) return null;
  return pallets;
}

export function toPerPalletWeightKg(
  averagePalletWeight: number | null | undefined,
  totalWeight: number | string | null | undefined,
  totalPallets: number | string | null | undefined,
  weightUnit: string | undefined | null
): number | null {
  if (
    typeof averagePalletWeight === 'number' &&
    Number.isFinite(averagePalletWeight) &&
    averagePalletWeight > 0
  ) {
    return toKilograms(averagePalletWeight, weightUnit);
  }
  const consignmentWeightKg = toTotalWeightKg(totalWeight, weightUnit);
  const pallets = toPalletCount(totalPallets);
  if (consignmentWeightKg === null || pallets === null) return null;
  return consignmentWeightKg / pallets;
}
